import React, { useState, useEffect } from 'react';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import { Layout, Menu, Button, Dropdown, Avatar, Badge, Modal, theme } from 'antd';
import {
  DashboardOutlined,
  CalendarOutlined,
  UserOutlined,
  LogoutOutlined,
  MenuFoldOutlined,
  MenuUnfoldOutlined,
  TagsOutlined,
  ShoppingCartOutlined,
  StarOutlined,
  MessageOutlined,
  BellOutlined,
} from '@ant-design/icons';
import { useAuth } from '../../context/AuthContext';
import BotonActualizar from './BotonActualizar';
import useSidebarResponsive from './useSidebarResponsive';
import FondoSidebarMobile from './FondoSidebarMobile';
import { buyerService } from '../../services/api';
import { rutaRemates } from '../../utils/rutasAcceso';

const { Header, Sider, Content } = Layout;

const ClienteLayout = () => {
  const {
    esMobile, collapsed, alternar, cerrarSiEsMobile,
    propsSider, zIndexSider, marginLeftContenido,
  } = useSidebarResponsive();
  const [noLeidasCount, setNoLeidasCount] = useState(0);
  // Bienvenida post-alta. Va acá, en el layout, y no en cada pantalla: el
  // registro puede caer en /cliente/remates o en /cliente/dashboard segun venga
  // o no con el slug del remate, y el saludo tiene que salir en las dos.
  const [bienvenida, setBienvenida] = useState(false);
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const { token: { colorBgContainer } } = theme.useToken();

  // `navigate(..., { state: { bienvenida: true } })` desde el alta. Se limpia el
  // state al leerlo para que no vuelva a aparecer si la persona recarga o
  // navega hacia atras.
  useEffect(() => {
    if (location.state?.bienvenida) {
      setBienvenida(true);
      navigate(location.pathname + location.search, { replace: true, state: {} });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.state]);

  // Cargar conteo de notificaciones no leídas
  useEffect(() => {
    let vigente = true;
    const fetchNoLeidas = async () => {
      try {
        const res = await buyerService.getNoLeidasCount();
        if (vigente) setNoLeidasCount(res.data?.count || 0);
      } catch {
        // Silencioso a proposito: es un contador que se refresca cada minuto en
        // segundo plano. Un cartel de error por no poder pintar un badge seria
        // peor que no pintarlo. Ver Skills/react-rules.md, excepcion (c) —
        // mejora progresiva que degrada sola. NO es la (a): la (a) es
        // telemetria fire-and-forget, y esto pinta algo que el usuario mira.
      }
    };
    fetchNoLeidas();
    const intervalo = setInterval(fetchNoLeidas, 60000);
    // `vigente` evita setState despues de desmontar si la respuesta llega tarde.
    return () => { vigente = false; clearInterval(intervalo); };
  }, []);

  const menuItems = [
    {
      key: '/cliente/dashboard',
      icon: <DashboardOutlined />,
      label: 'Dashboard',
    },
    {
      key: '/cliente/mis-animales',
      icon: <TagsOutlined />,
      label: 'Mis Animales',
    },
    {
      key: '/cliente/remates',
      icon: <CalendarOutlined />,
      label: 'Remates',
    },
    {
      key: '/cliente/mis-ofertas',
      icon: <ShoppingCartOutlined />,
      label: 'Mis Ofertas',
    },
    {
      key: '/cliente/seguimiento',
      icon: <StarOutlined />,
      label: 'Seguimiento',
    },
    {
      key: '/cliente/tickets',
      icon: <MessageOutlined />,
      label: 'Tickets',
    },
    {
      key: '/cliente/mi-perfil',
      icon: <UserOutlined />,
      label: 'Mi Perfil',
    },
  ];

  const userMenuItems = [
    {
      key: 'profile',
      icon: <UserOutlined />,
      label: 'Mi Perfil',
    },
    {
      type: 'divider',
    },
    {
      key: 'logout',
      icon: <LogoutOutlined />,
      label: 'Cerrar Sesión',
      danger: true,
    },
  ];

  const handleMenuClick = ({ key }) => {
    navigate(key);
    // En mobile el sidebar tapa el contenido: si no se cierra solo, la pantalla
    // recien navegada queda abajo del menú y hace falta un tap de más.
    cerrarSiEsMobile();
  };

  const handleUserMenuClick = ({ key }) => {
    if (key === 'logout') {
      logout();
      navigate('/login');
    } else if (key === 'profile') {
      navigate('/cliente/mi-perfil');
    }
  };

  return (
    <Layout className="app-layout">
      <FondoSidebarMobile visible={esMobile && !collapsed} onClick={cerrarSiEsMobile} />

      {/* Sidebar */}
      <Sider
        trigger={null}
        collapsible
        {...propsSider}
        style={{
          overflow: 'auto',
          height: '100vh',
          position: 'fixed',
          left: 0,
          top: 0,
          bottom: 0,
          zIndex: zIndexSider,
        }}
      >
        {/* Logo */}
        <div className="logo">
          <h2 className={collapsed ? 'logo-collapsed' : ''}>
            {collapsed ? 'P' : 'PUJA'}
            {!collapsed && <span className="logo-bajada">Gestión de Cabañas</span>}
          </h2>
        </div>

        {/* Menú */}
        <Menu
          theme="dark"
          mode="inline"
          selectedKeys={[location.pathname]}
          items={menuItems}
          onClick={handleMenuClick}
        />
      </Sider>

      {/* Layout principal */}
      <Layout style={{ marginLeft: marginLeftContenido, transition: 'margin-left 0.2s' }}>
        {/* Header */}
        <Header
          style={{
            padding: '0 24px',
            background: colorBgContainer,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            position: 'sticky',
            top: 0,
            zIndex: 1,
            boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
          }}
        >
          {/* Botón colapsar */}
          <Button
            type="text"
            icon={collapsed ? <MenuUnfoldOutlined /> : <MenuFoldOutlined />}
            onClick={alternar}
            style={{ fontSize: '16px', width: 64, height: 64 }}
          />

          {/* Campana + avatar */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
            <BotonActualizar iconSize={20} />
            <Badge
              count={noLeidasCount}
              size="small"
              onClick={() => navigate('/cliente/notificaciones')}
              style={{ cursor: 'pointer' }}
            >
              <BellOutlined style={{ fontSize: 20, cursor: 'pointer' }} />
            </Badge>

            <Dropdown
              menu={{ items: userMenuItems, onClick: handleUserMenuClick }}
              placement="bottomRight"
            >
              <div style={{ cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 8 }}>
                <Avatar icon={<UserOutlined />} style={{ backgroundColor: '#1890ff' }} />
                <span style={{ color: '#333' }}>{user?.nom_usuario || 'Usuario'}</span>
              </div>
            </Dropdown>
          </div>
        </Header>

        {/* Contenido */}
        <Content className="app-content">
          <Outlet />
        </Content>
      </Layout>

      {/* Bienvenida post-alta. Lo importante no es el saludo: es avisarle que
          todavía NO puede ofertar. Sin esto la persona se registra, entra, ve
          el catálogo y recién descubre el límite cuando intenta ofertar. */}
      <Modal
        open={bienvenida}
        onCancel={() => setBienvenida(false)}
        // Se conserva el ?evento=: Register manda a /cliente/remates?evento=<slug>
        // y esa pantalla resalta el remate. Navegar sin el parametro apagaba
        // el resaltado de la pantalla en la que la persona YA estaba parada.
        onOk={() => { setBienvenida(false); navigate(rutaRemates(new URLSearchParams(location.search).get('evento'))); }}
        okText="Ver remates"
        cancelText="Ahora no"
        title={`¡Te damos la bienvenida, ${user?.nom_usuario || ''}!`}
      >
        <p>Tu cuenta ya está creada.</p>
        <p>
          Para <strong>pre-ofertar</strong> falta un paso: la cabaña que organiza
          el remate tiene que <strong>habilitar tu usuario</strong>. Es la cabaña
          la que autoriza a quién le compra.
        </p>
        <p style={{ marginBottom: 0 }}>
          Mientras tanto podés recorrer el catálogo y las fichas sin problema. En{' '}
          <strong>Remates</strong> ves el estado de tu pedido y podés pedir acceso
          a otras cabañas.
        </p>
      </Modal>
    </Layout>
  );
};

export default ClienteLayout;
